import type { Dispatch, SetStateAction } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { api } from '@koridor/api-client';
import type { GameConfig } from '../contexts/GameContext';
import { buildRematchGameCfg } from './rematch';
import { buildGameLaunch, launchGame } from './launch-game';

export type RematchMatchedPayload = Parameters<typeof buildRematchGameCfg>[0];

let enteredRematchMatchId: string | null = null;
let lastRematchMatched: { payload: RematchMatchedPayload; at: number } | null = null;
let wired = false;

const REMATCH_REPLAY_MS = 180_000;

export function clearRematchMatchedBuffer() {
  lastRematchMatched = null;
}

export function resetRematchEntryLock() {
  enteredRematchMatchId = null;
  lastRematchMatched = null;
}

export function enterRematchOnce(
  payload: RematchMatchedPayload,
  navigate: NavigateFunction,
  setGameCfg: Dispatch<SetStateAction<GameConfig>>,
): boolean {
  const matchId = payload?.matchId;
  if (!matchId) return false;
  if (enteredRematchMatchId === matchId) return false;
  enteredRematchMatchId = matchId;
  launchGame(buildGameLaunch(buildRematchGameCfg(payload)), navigate, setGameCfg);
  return true;
}

type RematchHandler = (payload: RematchMatchedPayload) => void;
const rematchHandlers = new Set<RematchHandler>();

export function onRematchMatched(handler: RematchHandler) {
  rematchHandlers.add(handler);
  if (lastRematchMatched && Date.now() - lastRematchMatched.at < REMATCH_REPLAY_MS) {
    queueMicrotask(() => handler(lastRematchMatched!.payload));
  }
  return () => { rematchHandlers.delete(handler); };
}

/** Single WS listener for rematch.matched — avoids duplicate launchGame calls. */
export function ensureRematchMatchedSignal() {
  if (wired) return () => {};
  wired = true;
  const off = api.on('rematch.matched', (payload: unknown) => {
    const p = payload as RematchMatchedPayload;
    if (!p?.matchId) return;
    lastRematchMatched = { payload: p, at: Date.now() };
    for (const handler of rematchHandlers) handler(p);
  });
  return () => {
    off();
    wired = false;
    lastRematchMatched = null;
  };
}
